import { useState, useCallback, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { supabase } from '@/lib/supabase'
import { toast } from '@/components/ui/use-toast'
import { useAuth } from '@/lib/auth.tsx'
import { motion } from 'framer-motion'
import { AlertCircle } from 'lucide-react'

type Message = {
    role: 'user' | 'assistant'
    content: string
}

interface ChatInterfaceProps {
    contentId: string
    onVersionCreated?: (versionNumber: number) => void
    autoFocus?: boolean
    onFocused?: () => void
    onError?: (error: string) => void
}

export function ChatInterface({ contentId, onVersionCreated, autoFocus, onFocused, onError }: ChatInterfaceProps) {
    const [messages, setMessages] = useState<Message[]>([{
        role: 'assistant',
        content: 'How can I help improve this content?'
    }])
    const [input, setInput] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const { session } = useAuth()

    // Scroll to bottom of messages when new messages are added
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [messages])

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus()
            onFocused?.()
        }
    }, [autoFocus, onFocused])

    const addMessage = useCallback((message: Message) => {
        setMessages(prev => [...prev, message])
    }, [])

    const handleResponse = useCallback(async (responseData: any) => {
        // Check if chat_response exists in the response
        if (responseData.chat_response || responseData.chatResponse) {
            // Add the assistant's response to messages
            addMessage({
                role: 'assistant',
                content: responseData.chat_response || responseData.chatResponse
            })
        }

        // Check if a new version was created
        if (responseData.version_id) {
            try {
                // Get the version number from Supabase
                const { data: version, error: versionError } = await supabase
                    .from('content_versions')
                    .select('version_number')
                    .eq('id', responseData.version_id)
                    .single()

                if (versionError) throw versionError

                const versionNumber = version?.version_number

                // Notify parent component with the version number
                if (versionNumber) {
                    
                    // Notify parent component - this might cause content reload
                    onVersionCreated?.(versionNumber)
                    
                    // After a short delay, ensure messages are preserved after reload
                    setTimeout(() => {
                        setMessages(currentMessages => {
                            // Add a success message about version creation if not already included
                            if (!currentMessages.some(msg => 
                                msg.role === 'assistant' && 
                                (msg.content.includes(`version ${versionNumber}`) || 
                                 msg.content.includes('updated successfully')))
                            ) {
                                return [
                                    ...currentMessages,
                                    // {
                                    //     role: 'assistant',
                                    //     content: `✅ Content updated successfully (version ${versionNumber})`
                                    // }
                                ]
                            }
                            return currentMessages
                        })
                    }, 100)
                }
            } catch (error) {
                console.error('Error getting version number:', error)
                // Still notify parent even if we couldn't get the version number
                onVersionCreated?.(0)
            }
        }
    }, [addMessage, messages, onVersionCreated])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!input.trim() || isLoading) return

        const userMessage = { role: 'user' as const, content: input }
        addMessage(userMessage)
        setInput('')
        setIsLoading(true)

        try {
            const workerUrl = import.meta.env.VITE_WORKERS_URL
            const endpointUrl = `${workerUrl}/rainfactory/content-functions/improve-content`

            const response = await fetch(endpointUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session?.access_token}`,
                    'Content-Type': 'application/json',
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
                },
                body: JSON.stringify({
                    content_id: contentId,
                    conversation: [...messages, userMessage]
                }),
            })

            if (!response.ok) {
                // Get error details if available
                let errorDetail = 'Failed to improve content'
                try {
                    const errorResponse = await response.json()
                    errorDetail = errorResponse.error || errorDetail
                } catch (e) {
                    // If we can't parse the error, use the status text
                    errorDetail = response.statusText || errorDetail
                }
                
                // Notify the parent component of the error
                onError?.(errorDetail)
                
                // Show error in chat interface
                addMessage({
                    role: 'assistant',
                    content: `⚠️ ${errorDetail}. Please try again or rephrase your request.`
                })
                
                // Also show toast for visibility
                toast({
                    title: "Error",
                    description: errorDetail,
                    variant: "destructive",
                })
                return
            }

            const data = await response.json()
            
            if (!data.data) {
                throw new Error('Invalid response format')
            }

            // Process the response data
            await handleResponse(data.data)

        } catch (error: any) {
            console.error('Error improving content:', error)
            const errorMessage = error?.message || 'Failed to improve content'
            
            // Notify parent of error
            onError?.(errorMessage)
            
            // Show error in chat
            addMessage({
                role: 'assistant',
                content: `⚠️ ${errorMessage}. Please try again or contact support if the issue persists.`
            })
            
            toast({
                title: "Error",
                description: errorMessage,
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    // Handle pressing Enter to submit
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            if (input.trim() && !isLoading) {
                handleSubmit(e as unknown as React.FormEvent)
            }
        }
    }

    return (
        <div className="flex flex-col h-full">
            <ScrollArea className="flex-1 p-4 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
                <div className="space-y-4">
                    {messages.map((message, i) => (
                        <div
                            key={i}
                            className={`flex ${message.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
                        >
                            <div
                                className={`rounded-lg px-4 py-2 max-w-[85%] shadow-sm ${
                                    message.role === 'assistant'
                                        ? 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
                                        : 'bg-primary text-primary-foreground'
                                }`}
                            >
                                {/* Split message by newlines to properly render them */}
                                {message.content.split('\n').map((line, idx) => (
                                    <span key={idx}>
                                        {line}
                                        {idx !== message.content.split('\n').length - 1 && <br />}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                    {isLoading && (
                        <div className="flex justify-start">
                            <motion.div 
                                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-2 flex items-center gap-2 shadow-sm"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ 
                                    opacity: 1, 
                                    y: 0,
                                    transition: {
                                        duration: 0.3,
                                        ease: "easeOut"
                                    }
                                }}
                            >
                                <motion.div
                                    animate={{
                                        y: [0, -4, 0],
                                    }}
                                    transition={{
                                        duration: 1.5,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                    className="w-2 h-2 bg-primary rounded-full"
                                />
                                <motion.div
                                    animate={{
                                        y: [0, -4, 0],
                                    }}
                                    transition={{
                                        duration: 1.5,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                        delay: 0.2
                                    }}
                                    className="w-2 h-2 bg-primary rounded-full"
                                />
                                <motion.div
                                    animate={{
                                        y: [0, -4, 0],
                                    }}
                                    transition={{
                                        duration: 1.5,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                        delay: 0.4
                                    }}
                                    className="w-2 h-2 bg-primary rounded-full"
                                />
                                <span className="text-foreground">Thinking...</span>
                            </motion.div>
                        </div>
                    )}
                    {/* Reference for scrolling to bottom */}
                    <div ref={messagesEndRef} />
                </div>
            </ScrollArea>

            <form onSubmit={handleSubmit} className="p-4 border-t bg-background">
                <div className="flex gap-2">
                    <Input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type your message..."
                        disabled={isLoading}
                        ref={inputRef}
                        className="border-slate-200 dark:border-slate-700"
                    />
                    <Button type="submit" disabled={isLoading}>
                        Send
                    </Button>
                </div>
                <div className="mt-2 text-xs text-muted-foreground flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Hint: Ask specific questions about the content or request improvements
                </div>
            </form>
        </div>
    )
}